import { Spotlight, SpotlightActionData } from '@mantine/spotlight';
import { Search } from 'lucide-react';
import { useFirm } from '@/database/hooks/hooks';
import { useMemo, useState, useRef, useEffect, useDeferredValue } from 'react';
import TimeEntryModal from '../TimeEntryModal/TimeEntryModal';
import ExpenseEntryModal from '../ExpenseEntryModal/ExpenseEntryModal';
import TaskModal from '../TaskModal/TaskModal';
import NotesModal from '../NotesModal';
import AddEditMatterModal from '../AddEditMatter/AddEditMatterModal';
import { levenshteinDistance, truncateMiddle } from './utils';
import TransactionEntryModal from '../TransactionEntryModal/TransactionEntryModal';
import { useNavigate } from 'react-router-dom';
import { Group, Loader, ScrollArea } from '@mantine/core';
import AragSearchModal from '../AragSearchModal/AragSearchModal';
import type { AddEditMatterFormData } from '../AddEditMatter/types';
import { FlattenedMatter } from '@/database/aggregateTypes';
import type { LoadState } from '@/hooks/useLoadState';
import { makeLoadState } from '@/hooks/useLoadState';
import { useInsuranceCases } from '@/database/hooks/insuranceCases';
import { InsuranceCaseDocument, MatterDocument } from '@shared/types';
import { useMatterSearch } from '@/contexts/MatterSearchContext';

type SpotlightActionDataWithPattern = SpotlightActionData & {
  pattern?: RegExp;
  setMatchedValue?: (value: string) => void;
};

type AddEditMatterModalState =
  | { status: 'closed' }
  | {
      status: 'open';
      flattenedMatter: FlattenedMatter | undefined;
      overrides: Partial<AddEditMatterFormData> | undefined;
    };

export function SpotlightSearch({
  children,
  onOpenChat,
}: {
  children: React.ReactNode;
  onOpenChat?: (initialText?: string) => void;
}) {
  const { firm } = useFirm();
  const { ensureMatterSearchStarted } = useMatterSearch();
  const isAragDemoMode = firm?.demoFlags?.arag === true;
  const [hasActivatedMatterActions, setHasActivatedMatterActions] =
    useState(false);
  const { loadState: matterActionsLoadState, isInsuranceEnrichmentPending } =
    useMatterActions(hasActivatedMatterActions);
  const [hasTyped, setHasTyped] = useState(false);
  const [query, setQuery] = useState('');
  const [actionsListHeight, setActionsListHeight] = useState(0);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const [timeEntryModalOpen, setTimeEntryModalOpen] = useState(false);
  const [expenseEntryModalOpen, setExpenseEntryModalOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [addEditMatterModalState, setAddEditMatterModalState] =
    useState<AddEditMatterModalState>({ status: 'closed' });
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [aragSearchModalStatus, setAragSearchModalStatus] = useState<
    'empty' | 'caseAssistNumber' | 'memberId' | 'hidden'
  >('hidden');
  const [matchedAragValue, setMatchedAragValue] = useState<string>();

  // Defer the query value to keep input responsive while heavy filtering runs
  const deferredQuery = useDeferredValue(query);

  // Create quick actions with keywords
  const quickActions: SpotlightActionDataWithPattern[] = useMemo(
    () => [
      {
        id: 'new-matter',
        label: 'New Matter',
        description: 'Create / add new matter',
        keywords: [
          'create',
          'add',
          'new',
          'matter',
          'client',
          'case',
          'person',
          'company',
        ],
        onClick: () =>
          setAddEditMatterModalState({
            status: 'open',
            flattenedMatter: undefined,
            overrides: undefined,
          }),
      },
      {
        id: 'new-time-entry',
        label: 'New Time Entry',
        description: 'Create / add new time entry',
        keywords: [
          'create',
          'add',
          'new',
          'time',
          'entry',
          'hours',
          'timesheet',
          'log',
          'hourly',
        ],
        onClick: () => setTimeEntryModalOpen(true),
      },
      {
        id: 'new-expense-entry',
        label: 'New Expense Entry',
        description: 'Create / add new expense entry',
        keywords: [
          'create',
          'add',
          'new',
          'expense',
          'cost',
          'payment',
          'receipt',
          'bill',
        ],
        onClick: () => setExpenseEntryModalOpen(true),
      },
      {
        id: 'new-task',
        label: 'New Task',
        description: 'Create / add new task',
        keywords: [
          'create',
          'add',
          'new',
          'task',
          'todo',
          'assignment',
          'work',
          'project',
          'to-do',
        ],
        onClick: () => setTaskModalOpen(true),
      },
      {
        id: 'new-note',
        label: 'New Note',
        description: 'Create / add new note',
        keywords: [
          'create',
          'add',
          'new',
          'note',
          'memo',
          'comment',
          'write',
          'record',
        ],
        onClick: () => setNotesModalOpen(true),
      },
      {
        id: 'new-transaction',
        label: 'New Transaction',
        description: 'Create / add new transaction',
        keywords: [
          'create',
          'add',
          'new',
          'transaction',
          'money',
          'payment',
          'deposit',
        ],
        onClick: () => setShowTransactionModal(true),
      },
      {
        id: 'find-arag-case',
        label: 'Find ARAG Case ',
        description: 'Find an ARAG case by CaseAssist number or member ID',
        keywords: [
          'arag',
          'case',
          'assist',
          'number',
          'search',
          'find',
          'case-assist',
          'caseassist',
          'member-id',
          'memberid',
          'insurance',
        ],

        onClick: () => {
          setAragSearchModalStatus('empty');
        },
      },
      {
        id: 'find-arag-case-by-case-assist-number',
        label: 'Find ARAG Case',
        description: 'Find the ARAG case with this CaseAssist number',
        pattern: isAragDemoMode ? /^\d+$/ : /^\d{7}-\d{3}$/,
        setMatchedValue: setMatchedAragValue,
        onClick: () => {
          setAragSearchModalStatus('caseAssistNumber');
        },
      },
      {
        id: 'find-arag-case-by-member-id',
        label: "Find member's ARAG Cases",
        description: 'Find ARAG cases belonging to the member with this ID',
        pattern: /^\d{12}$/,
        setMatchedValue: setMatchedAragValue,
        onClick: () => {
          setAragSearchModalStatus('memberId');
        },
      },
    ],
    [isAragDemoMode],
  );

  // Filter actions based on search - optimized for performance
  const filteredMatterActions = useMemo(() => {
    if (!deferredQuery.trim() || matterActionsLoadState.status !== 'loaded') {
      return [];
    }
    const searchTerm = deferredQuery.toLowerCase().trim();

    // Gate matter results until user types at least 3 characters
    if (searchTerm.length <= 2) {
      return [];
    }

    const matterActions = matterActionsLoadState.data;

    // Quick exact matches
    const exactMatches = matterActions.filter((action) => {
      const label = (action.label || '').toLowerCase();
      const description = (action.description || '').toLowerCase();
      const keywords = ((action as any).keywords || []).join(' ').toLowerCase();
      return (
        label.includes(searchTerm) ||
        description.includes(searchTerm) ||
        keywords.includes(searchTerm)
      );
    });

    // If we have enough exact matches, sort lightly and cap
    if (exactMatches.length >= 20) {
      return exactMatches
        .sort((a, b) => {
          const aLabel = (a.label || '').toLowerCase();
          const bLabel = (b.label || '').toLowerCase();
          const aHit = aLabel.includes(searchTerm);
          const bHit = bLabel.includes(searchTerm);
          if (aHit && !bHit) return -1;
          if (!aHit && bHit) return 1;
          return 0;
        })
        .slice(0, 20);
    }
    const exactMatchIds = exactMatches.map((action) => action.id);

    // Fuzzy match on a limited candidate set
    const candidates = matterActions.slice(0, 200); // cap work
    const fuzzyMatches = candidates.filter((action) => {
      if (exactMatchIds.includes(action.id)) {
        return false;
      }
      const label = (action.label || '').toLowerCase();
      const labelWords = label.split(/[\s-]+/);
      const searchWords = searchTerm.split(' ');
      return searchWords.every((searchWord) =>
        labelWords.some(
          (labelWord) =>
            labelWord.includes(searchWord) ||
            (labelWord.length > 2 &&
              searchWord.length > 2 &&
              levenshteinDistance(labelWord, searchWord) < 3),
        ),
      );
    });

    return [...exactMatches, ...fuzzyMatches]
      .slice(0, 50) // limit before sorting
      .sort((a, b) => {
        const aLabel = (a.label || '').toLowerCase();
        const bLabel = (b.label || '').toLowerCase();
        const aHit = aLabel.includes(searchTerm);
        const bHit = bLabel.includes(searchTerm);
        if (aHit && !bHit) return -1;
        if (!aHit && bHit) return 1;
        return 0;
      })
      .slice(0, 20);
  }, [matterActionsLoadState, deferredQuery]);

  const filteredQuickActions = useMemo(() => {
    if (!deferredQuery.trim()) return quickActions.slice(0, 10);
    const searchTerm = deferredQuery.toLowerCase().trim();

    // Fast path for short queries
    if (searchTerm.length <= 2) {
      return quickActions
        .filter((action) => {
          const label = (action.label || '').toLowerCase();
          return label.includes(searchTerm);
        })
        .slice(0, 5);
    }

    return quickActions
      .filter((action) => {
        if (action.pattern) {
          const isMatch = action.pattern.test(deferredQuery);
          if (isMatch) {
            action.setMatchedValue?.(deferredQuery);
          }
          return isMatch;
        }

        const label = (action.label || '').toLowerCase();
        const description = (action.description || '').toLowerCase();
        const keywords = ((action as any).keywords || [])
          .join(' ')
          .toLowerCase();

        // Only exact substring matching for quick actions to avoid false positives
        return (
          label.includes(searchTerm) ||
          description.includes(searchTerm) ||
          keywords.includes(searchTerm)
        );
      })
      .slice(0, 50) // cap work before sorting
      .sort((a, b) => {
        const aLabel = (a.label || '').toLowerCase();
        const bLabel = (b.label || '').toLowerCase();

        if (aLabel.includes(searchTerm) && !bLabel.includes(searchTerm))
          return -1;
        if (!aLabel.includes(searchTerm) && bLabel.includes(searchTerm))
          return 1;

        return 0;
      })
      .slice(0, 7); // top results to display
  }, [quickActions, deferredQuery]);

  // Combine actions for display based on search state
  const actionsToDisplay = useMemo(() => {
    if (!hasTyped) return [];
    const askScoutAction = [
      {
        id: 'ask-scout-ai',
        label: 'Ask Scout AI',
        description: 'Open AI chat with your query',
        group: 'Ask Scout AI',
        onClick: () => {
          if (onOpenChat) {
            onOpenChat(query);
          }
        },
      } as SpotlightActionData,
    ];

    return [
      ...filteredQuickActions.map((action) => ({
        ...action,
        group: 'Quick Actions',
      })),
      ...filteredMatterActions.map((action) => ({
        ...action,
        group: 'Matters',
      })),
      ...askScoutAction,
    ];
  }, [filteredQuickActions, filteredMatterActions, hasTyped]);

  const noMatches =
    hasTyped &&
    matterActionsLoadState.status !== 'loading' &&
    !isInsuranceEnrichmentPending &&
    filteredQuickActions.length === 0 &&
    filteredMatterActions.length === 0;

  // Calculate dynamic height based on number of items
  useEffect(() => {
    if (!hasTyped || actionsToDisplay.length === 0) {
      setActionsListHeight(0);
      return;
    }

    // Estimate height: ~50px per item + 40px per group header
    // Count groups for headers
    const groups = new Set(actionsToDisplay.map((item) => item.group));
    const groupCount = groups.size;

    // Calculate total height (items + group headers)
    const estimatedHeight = actionsToDisplay.length * 50 + groupCount * 40;

    // Cap at 350px maximum, ensure at least 100px minimum if we have items
    const finalHeight =
      actionsToDisplay.length > 0
        ? Math.min(350, Math.max(100, estimatedHeight))
        : 0;

    setActionsListHeight(finalHeight);
  }, [actionsToDisplay, hasTyped]);

  const renderActions = () => {
    if (!hasTyped || actionsToDisplay.length === 0) {
      return null;
    }

    // Group actions by their group property
    const groupedActions: Record<string, SpotlightActionData[]> = {};

    actionsToDisplay.forEach((action) => {
      const group = action.group as string;
      if (!groupedActions[group]) {
        groupedActions[group] = [];
      }
      groupedActions[group].push(action);
    });

    return Object.entries(groupedActions).map(([group, actions]) => (
      <Spotlight.ActionsGroup key={group} label={group}>
        {actions.map((action, index) => (
          <Spotlight.Action
            key={`${action.id}-${index}`}
            onClick={action.onClick}
            label={action.label || ''}
            description={action.description}
          />
        ))}
      </Spotlight.ActionsGroup>
    ));
  };

  return (
    <>
      {/* Using compound components */}
      <Spotlight.Root
        shortcut='mod + k'
        query={query}
        onSpotlightOpen={() => {
          ensureMatterSearchStarted();
          setHasActivatedMatterActions(true);
        }}
        onQueryChange={(q) => {
          setQuery(q);
          setHasTyped(q.length > 0);
        }}
        onSpotlightClose={() => {
          setHasTyped(false);
          setQuery('');
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
          const searchButton = document.querySelector('.search-button');
          if (searchButton instanceof HTMLElement) {
            searchButton.blur();
          }
        }}
        withOverlay
        classNames={{
          action: 'spotlight-action-with-hover',
        }}
        styles={{
          actionsList: {
            display: hasTyped ? 'block' : 'none',
          },
          search: {
            paddingLeft: '48px',
            fontSize: 'var(--mantine-font-size-lg)',
          },
        }}
      >
        <style>
          {`
            /* Hover state */
            .spotlight-action-with-hover:hover {
              background-color: rgba(0, 0, 0, 0.05) !important;
            }

            /* Focus state */
            .spotlight-action-with-hover:focus {
              background-color: rgba(0, 0, 0, 0.05) !important;
            }

            /* Selected state - the item that will be activated when Enter is pressed */
            .spotlight-action-with-hover[data-selected="true"] {
              color: var(--mantine-color-text);
              background-color: rgba(0, 0, 0, 0.08);
              font-weight: 500 !important;
              border-left: 3px solid #ff00bf !important;
              padding-left: calc(var(--mantine-spacing-md) - 3px) !important;
            }

            /* Make description text darker in selected items (light mode) */
            .spotlight-action-with-hover[data-selected="true"] .mantine-Spotlight-actionDescription {
              color: rgba(0, 0, 0, 0.8) !important;
              opacity: 0.9 !important;
            }

            /* Dark mode styles */
            [data-mantine-color-scheme="dark"] .spotlight-action-with-hover:hover,
            [data-mantine-color-scheme="dark"] .spotlight-action-with-hover:focus {
              background-color: rgba(255, 255, 255, 0.1) !important;
            }

            [data-mantine-color-scheme="dark"] .spotlight-action-with-hover[data-selected="true"] {
              background-color: rgba(255, 255, 255, 0.15) !important;
              font-weight: 500 !important;
              border-left: 3px solid #ff00bf !important;
              padding-left: calc(var(--mantine-spacing-md) - 3px) !important;
            }

            /* Brighten description text in selected items (dark mode) */
            [data-mantine-color-scheme="dark"] .spotlight-action-with-hover[data-selected="true"] .mantine-Spotlight-actionDescription {
              color: rgba(255, 255, 255, 0.9) !important;
              opacity: 0.9 !important;
            }

            /* Proper spacing between results */
            .mantine-Spotlight-action {
              margin-bottom: var(--mantine-spacing-xs) !important;
            }

            .mantine-Spotlight-actionsGroup {
              margin-bottom: var(--mantine-spacing-md) !important;
            }

            /* Hide completely when no results */
            .mantine-Spotlight-actionsList:empty {
              display: none !important;
            }
          `}
        </style>
        <Spotlight.Search
          placeholder='Search or do anything...'
          leftSection={<Search size={20} />}
        />
        <Spotlight.ActionsList>
          {hasTyped && actionsToDisplay.length > 0 ? (
            <>
              {matterActionsLoadState.status === 'loading' &&
                deferredQuery.trim().length >= 3 && (
                  <Spotlight.Empty>
                    <Group gap='xs' justify='center'>
                      <Loader type='dots' size='lg' color='pink.4' />
                      <span>Loading matters...</span>
                    </Group>
                  </Spotlight.Empty>
                )}
              {noMatches && <Spotlight.Empty>No results</Spotlight.Empty>}
              {actionsToDisplay.length > 7 ? (
                <ScrollArea
                  h={actionsListHeight}
                  type='always'
                  offsetScrollbars
                  ref={scrollAreaRef}
                >
                  {renderActions()}
                </ScrollArea>
              ) : (
                renderActions()
              )}
            </>
          ) : hasTyped && query.trim() ? (
            <Spotlight.Empty>Nothing found</Spotlight.Empty>
          ) : null}
        </Spotlight.ActionsList>
      </Spotlight.Root>

      {timeEntryModalOpen && (
        <TimeEntryModal
          fixedMatterId={undefined}
          onClose={() => setTimeEntryModalOpen(false)}
        />
      )}

      {expenseEntryModalOpen && (
        <ExpenseEntryModal
          fixedMatterId={undefined}
          onClose={() => setExpenseEntryModalOpen(false)}
        />
      )}

      {taskModalOpen && (
        <TaskModal
          fixedMatterId={undefined}
          onClose={() => setTaskModalOpen(false)}
        />
      )}

      {notesModalOpen && (
        <NotesModal
          fixedMatterId={undefined}
          onClose={() => setNotesModalOpen(false)}
        />
      )}

      {addEditMatterModalState.status === 'open' && (
        <AddEditMatterModal
          matter={addEditMatterModalState.flattenedMatter}
          initialOverrides={addEditMatterModalState.overrides}
          onClose={() => {
            setAddEditMatterModalState({ status: 'closed' });
          }}
        />
      )}

      {showTransactionModal && (
        <TransactionEntryModal onClose={() => setShowTransactionModal(false)} />
      )}

      {aragSearchModalStatus !== 'hidden' && (
        <AragSearchModal
          initialCaseAssistNumber={
            aragSearchModalStatus === 'caseAssistNumber'
              ? matchedAragValue?.trim()
              : undefined
          }
          initialMemberId={
            aragSearchModalStatus === 'memberId'
              ? matchedAragValue?.trim()
              : undefined
          }
          onClose={() => setAragSearchModalStatus('hidden')}
          onShowAddEditMatterModal={({ flattenedMatter, overrides }) => {
            setAragSearchModalStatus('hidden');
            setAddEditMatterModalState({
              status: 'open',
              flattenedMatter,
              overrides,
            });
          }}
        />
      )}

      {children}
    </>
  );
}

const useMatterActions = (
  enabled: boolean,
): {
  loadState: LoadState<SpotlightActionData[]>;
  isInsuranceEnrichmentPending: boolean;
} => {
  const {
    loading: mattersLoading,
    error: mattersError,
    matters,
  } = useMatterSearch();
  const insuranceCasesLoadState = useInsuranceCases({ enabled });
  const navigate = useNavigate();
  const loadState = useMemo<LoadState<SpotlightActionData[]>>(() => {
    if (!enabled) return makeLoadState({ status: 'loaded', data: [] });
    if (mattersLoading) return makeLoadState({ status: 'loading' });
    if (mattersError)
      return makeLoadState({ status: 'error', error: mattersError });
    const insuranceCasesByMatterId: Record<string, InsuranceCaseDocument[]> =
      {};
    if (insuranceCasesLoadState.status === 'loaded') {
      insuranceCasesLoadState.data.forEach((insuranceCase) => {
        if (!insuranceCase.matterId) {
          return;
        }
        if (!insuranceCasesByMatterId[insuranceCase.matterId]) {
          insuranceCasesByMatterId[insuranceCase.matterId] = [];
        }
        insuranceCasesByMatterId[insuranceCase.matterId].push(insuranceCase);
      });
    }
    const actions = matters.map((matter) => {
      const description = composeMatterDescription({
        matter,
        insuranceCases: insuranceCasesByMatterId[matter.id] ?? [],
      });
      return {
        id: matter.id,
        label: matter.name,
        description,
        keywords: (insuranceCasesByMatterId[matter.id] ?? []).flatMap(
          (insuranceCase) =>
            insuranceCase.memberId ? [insuranceCase.memberId] : [],
        ),
        onClick: () => navigate(`/matter/${matter.id}`),
      };
    });
    return makeLoadState({
      status: 'loaded',
      data: actions,
    });
  }, [
    enabled,
    matters,
    mattersLoading,
    mattersError,
    insuranceCasesLoadState.snapshotId,
    navigate,
  ]);
  return {
    loadState,
    isInsuranceEnrichmentPending:
      enabled && insuranceCasesLoadState.status === 'loading',
  };
};

const composeMatterDescription = (props: {
  matter: MatterDocument;
  insuranceCases: InsuranceCaseDocument[];
}): string => {
  const { matter, insuranceCases } = props;
  const descriptionParts: string[] = [];
  if (matter.matterType) {
    descriptionParts.push(matter.matterType);
  }
  if (matter.scope) {
    descriptionParts.push(truncateMiddle(matter.scope, 80));
  }
  const caseIds = insuranceCases.flatMap((insuranceCase) =>
    insuranceCase.caseId ? [insuranceCase.caseId] : [],
  );

  if (caseIds.length > 0) {
    let descriptionPart =
      matter.matterType === 'ARAG' ? 'CaseAssist' : 'Case ID';
    descriptionPart += ` ${caseIds.join(';')}`;
    descriptionParts.push(descriptionPart);
  }
  return descriptionParts.join(' · ');
};
